import React, { useState } from "react";
import {
  MobileOutlined,
  TabletOutlined,
  LaptopOutlined,
  DesktopOutlined,
} from "@ant-design/icons";
import { SecondaryButton } from "../../design-system";
import { typography, surfaces, borders, textColors, interactive } from "../../design-system/tokens";
import { Z_INDEX } from '../../core/utils/zIndexScale';
import useMatchMedia from "../../core/hooks/useMatchMedia";

const DevicePreviewControls = ({
  mode,
  setMode,
  orientation,
  setOrientation,
  showFrame,
  setShowFrame,
  forceCompact = false,
}) => {
  const [deviceDropdownOpen, setDeviceDropdownOpen] = useState(false);
  // When the preview controls are in compact/dropdown mode, hide the active-mode label
  // on narrower desktop widths to keep the navbar clean.
  const hideActiveModeLabel = useMatchMedia("(max-width: 1450px)", false);

  return (
    <>
      {/* Device preview - Inline for wide desktop screens (xl and above) */}
      <div className={forceCompact ? "hidden" : "hidden xl:flex items-center"}>
        <div className="bg-white bg-opacity-10 rounded-lg flex items-stretch overflow-hidden">
          <button
            onClick={() => setMode("responsive")}
            className={`flex items-center justify-center px-2 py-2.5 transition-all duration-200 border-r border-white border-opacity-20 ${
              mode === "responsive" 
                ? "bg-white bg-opacity-20 text-white" 
                : "text-white text-opacity-70 hover:bg-white hover:bg-opacity-10 hover:text-opacity-100"
            }`}
            title="Responsive"
          >
            <svg
              className="w-3.5 h-3.5"
              viewBox="0 0 24 24"
              fill="currentColor"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path d="M23,2V12a1,1,0,0,1-2,0V4.414L4.414,21H12a1,1,0,0,1,0,2H2a1,1,0,0,1-1-1V12a1,1,0,0,1,2,0v7.586L19.586,3H12a1,1,0,0,1,0-2H22A1,1,0,0,1,23,2Z"></path>
            </svg>
          </button>
          <button
            onClick={() => setMode("phone")}
            className={`flex items-center justify-center px-2 py-2.5 transition-all duration-200 border-r border-white border-opacity-20 ${
              mode === "phone" 
                ? "bg-white bg-opacity-20 text-white" 
                : "text-white text-opacity-70 hover:bg-white hover:bg-opacity-10 hover:text-opacity-100"
            }`}
            title="Phone"
          >
            <MobileOutlined style={{ fontSize: 14 }} />
          </button>
          <button
            onClick={() => setMode("tablet")}
            className={`flex items-center justify-center px-2 py-2.5 transition-all duration-200 border-r border-white border-opacity-20 ${
              mode === "tablet" 
                ? "bg-white bg-opacity-20 text-white" 
                : "text-white text-opacity-70 hover:bg-white hover:bg-opacity-10 hover:text-opacity-100"
            }`}
            title="Tablet"
          >
            <TabletOutlined style={{ fontSize: 14 }} />
          </button>
          <button
            onClick={() => setMode("laptop")}
            className={`flex items-center justify-center px-2 py-2.5 transition-all duration-200 border-r border-white border-opacity-20 ${
              mode === "laptop" 
                ? "bg-white bg-opacity-20 text-white" 
                : "text-white text-opacity-70 hover:bg-white hover:bg-opacity-10 hover:text-opacity-100"
            }`}
            title="Laptop"
          >
            <LaptopOutlined style={{ fontSize: 14 }} />
          </button>
          <button
            onClick={() => setMode("desktop")}
            className={`flex items-center justify-center px-2 py-2.5 transition-all duration-200 ${
              mode === "desktop" 
                ? "bg-white bg-opacity-20 text-white" 
                : "text-white text-opacity-70 hover:bg-white hover:bg-opacity-10 hover:text-opacity-100"
            }`}
            title="Desktop"
          >
            <DesktopOutlined style={{ fontSize: 14 }} />
          </button>
        </div>
      </div>
      
      {/* Device preview dropdown for screens smaller than wide desktop (below xl) */}
      <div
        className={[
          // When forced compact (e.g. ~1200px), don't make this container `relative`
          // so the dropdown can anchor against the navbar's `relative` container instead.
          forceCompact ? "flex items-center gap-2" : "sm:relative flex xl:hidden items-center gap-2",
        ].join(" ")}
      >
        <button
          onClick={() => setDeviceDropdownOpen(!deviceDropdownOpen)}
          className="flex items-center gap-2 p-2 text-white rounded-lg hover:bg-white hover:bg-opacity-10 transition-colors"
          title="Device preview"
          aria-label="Device preview"
        >
          {/* Green pill (only when not responsive) */}
          {mode !== "responsive" && !hideActiveModeLabel && (
            <span className="hidden sm:inline px-2 py-0.5 bg-green-400 text-white text-xs font-medium rounded">
              {mode === "phone" && "Phone"}
              {mode === "tablet" && "Tablet"}
              {mode === "laptop" && "Laptop"}
              {mode === "desktop" && "Desktop"}
            </span>
          )}
          {/* Icon */}
          <svg
            className="w-4 h-4"
            viewBox="0 0 478.306 478.306"
            xmlns="http://www.w3.org/2000/svg"
            fill="currentColor"
            aria-hidden="true"
          >
            <g>
              <path d="M86.649,65.425c0-1.386,1.128-2.506,2.513-2.506h318.124c1.386,0,2.513,1.12,2.513,2.506v215.91 c0,1.386-1.127,2.505-2.513,2.505H243.98v31.649h163.306c18.839,0,34.164-15.322,34.164-34.154V65.425 c0-18.832-15.324-34.155-34.164-34.155H89.163c-18.839,0-34.164,15.324-34.164,34.155v69.297h31.65V65.425z" />
              <path d="M465.888,328.441H243.98v44.96h201.792c17.97,0,32.534-14.564,32.534-32.542 C478.306,333.999,472.747,328.441,465.888,328.441z" />
              <path d="M175.27,166.371H37.061C16.625,166.371,0,182.994,0,203.431v206.545 c0,20.436,16.625,37.061,37.061,37.061H175.27c20.437,0,37.061-16.625,37.061-37.061V203.431 C212.331,182.994,195.707,166.371,175.27,166.371z M31.65,203.431c0-2.982,2.426-5.411,5.411-5.411H175.27 c2.984,0,5.411,2.428,5.411,5.411v191.825H31.65V203.431z M106.163,432.894c-4.997,0-9.202-2.928-11.331-7.086 c-0.899-1.752-1.526-3.664-1.526-5.77c0-7.103,5.755-12.858,12.858-12.858c7.101,0,12.856,5.755,12.856,12.858 c0,2.105-0.625,4.017-1.526,5.77C115.362,429.966,111.158,432.894,106.163,432.894z" />
            </g>
          </svg>
        </button>
        
        {deviceDropdownOpen && (
          <div className={`absolute left-1/2 -translate-x-1/2 top-full mt-2 w-[min(20rem,calc(100vw-24px))] sm:w-80 ${surfaces.elevated} rounded-lg shadow-xl border ${borders.default}`} style={{ zIndex: Z_INDEX.DROPDOWN }}>
            <div className="p-4">
              <h3 className={`${typography.h5} mb-4`}>Device Preview</h3>
              
              {/* Device mode selector */}
              <div className="mb-4">
                <label className={`block ${typography.label} mb-2`}>Preview Mode</label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { key: "responsive", label: "Responsive", icon: null },
                    { key: "phone", label: "Phone", icon: <MobileOutlined /> },
                    { key: "tablet", label: "Tablet", icon: <TabletOutlined /> },
                    { key: "laptop", label: "Laptop", icon: <LaptopOutlined /> },
                    { key: "desktop", label: "Desktop", icon: <DesktopOutlined /> },
                  ].map((opt) => (
                    <button
                      key={opt.key}
                      onClick={() => setMode(opt.key)}
                      className={`flex items-center justify-center gap-2 px-3 py-2 ${typography.bodySmall} border rounded-md transition-colors ${
                        mode === opt.key 
                          ? "border-blue-500 bg-blue-50 text-blue-700" 
                          : `${borders.strong} ${textColors.secondary} ${surfaces.hover}`
                      }`}
                    >
                      {opt.icon}
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Orientation (for phone/tablet only) */}
              {(mode === "phone" || mode === "tablet") && (
                <div className="mb-4">
                  <label className={`block ${typography.label} mb-2`}>Orientation</label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setOrientation("portrait")}
                      className={`flex-1 px-3 py-2 ${typography.bodySmall} border rounded-md transition-colors ${
                        orientation === "portrait" 
                          ? "border-blue-500 bg-blue-50 text-blue-700" 
                          : `${borders.strong} ${textColors.secondary} ${surfaces.hover}`
                      }`}
                    >
                      Portrait ↕
                    </button>
                    <button
                      onClick={() => setOrientation("landscape")}
                      className={`flex-1 px-3 py-2 ${typography.bodySmall} border rounded-md transition-colors ${
                        orientation === "landscape" 
                          ? "border-blue-500 bg-blue-50 text-blue-700" 
                          : `${borders.strong} ${textColors.secondary} ${surfaces.hover}`
                      }`}
                    >
                      Landscape ↔
                    </button>
                  </div>
                </div>
              )}

              {/* Frame toggle */}
              {mode !== "responsive" && (
                <div className="mb-4">
                  <label className="flex items-center justify-between">
                    <span className={typography.label}>Show Device Frame</span>
                    <button
                      onClick={() => setShowFrame(!showFrame)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        showFrame ? `${interactive.primaryBg}` : "bg-gray-200 dark:bg-dark-700"
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white dark:bg-dark-100 transition-transform ${
                          showFrame ? "translate-x-6" : "translate-x-1"
                        }`}
                      />
                    </button>
                  </label>
                </div>
              )}

              <div className="flex justify-end">
                <SecondaryButton
                  onClick={() => setDeviceDropdownOpen(false)}
                  variant="ghost"
                  className="text-sm"
                >
                  Close
                </SecondaryButton>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Click outside to close dropdown */}
      {deviceDropdownOpen && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setDeviceDropdownOpen(false)}
        />
      )}
    </>
  );
};

export default DevicePreviewControls;

